import { motion } from "framer-motion";
import { Shield, Clock, Users, DollarSign } from "lucide-react";
import { fadeIn, staggerContainer } from "@/lib/animations";

export function Benefits() {
  return (
    <section id="benefits" className="py-24 bg-gray-950">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <motion.div 
          variants={fadeIn("up", "tween", 0.2, 1)}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-light text-white">Why Choose Vettor</h2>
          <p className="mt-4 text-xl text-gray-400 font-light">AI-powered workflows to streamline your hiring process and find the perfect fit for your team</p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Benefit 1 */}
          <motion.div
            variants={fadeIn("up", "tween", 0.3, 1)}
            className="bg-gray-800 rounded-xl shadow-md p-8 border border-gray-700 hover:shadow-lg hover:bg-gray-750 transition-all duration-300"
          >
            <div className="w-12 h-12 bg-violet-900/30 text-violet-400 rounded-lg flex items-center justify-center mb-5">
              <Shield className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-light text-white mb-3">AI Automation</h3>
            <p className="text-gray-400 font-light">Our AI agents handle repetitive tasks like screening, scheduling, and follow-ups, saving your recruiting team hours every day.</p>
          </motion.div>
          
          {/* Benefit 2 */}
          <motion.div
            variants={fadeIn("up", "tween", 0.4, 1)}
            className="bg-gray-800 rounded-xl shadow-md p-8 border border-gray-700 hover:shadow-lg hover:bg-gray-750 transition-all duration-300"
          >
            <div className="w-12 h-12 bg-violet-900/30 text-violet-400 rounded-lg flex items-center justify-center mb-5">
              <Clock className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-light text-white mb-3">Faster Hiring</h3>
            <p className="text-gray-400 font-light">Reduce your time-to-hire by up to 60% with intelligent workflows that eliminate bottlenecks in your recruiting process.</p>
          </motion.div>
          
          {/* Benefit 3 */}
          <motion.div
            variants={fadeIn("up", "tween", 0.5, 1)}
            className="bg-gray-800 rounded-xl shadow-md p-8 border border-gray-700 hover:shadow-lg hover:bg-gray-750 transition-all duration-300"
          >
            <div className="w-12 h-12 bg-violet-900/30 text-violet-400 rounded-lg flex items-center justify-center mb-5">
              <Users className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-light text-white mb-3">Enhanced Experience</h3>
            <p className="text-gray-400 font-light">Provide a premium candidate experience with timely communication and feedback, all managed by our AI workflow.</p>
          </motion.div>
          
          {/* Benefit 4 */}
          <motion.div
            variants={fadeIn("up", "tween", 0.6, 1)}
            className="bg-gray-800 rounded-xl shadow-md p-8 border border-gray-700 hover:shadow-lg hover:bg-gray-750 transition-all duration-300"
          >
            <div className="w-12 h-12 bg-violet-900/30 text-violet-400 rounded-lg flex items-center justify-center mb-5">
              <DollarSign className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-light text-white mb-3">Cost Effective</h3>
            <p className="text-gray-400 font-light">Save up to 40% on recruiting costs by automating administrative tasks and optimizing your team's valuable time.</p>
          </motion.div>
        </div>
        
        <motion.div
          variants={fadeIn("up", "tween", 0.7, 1)}
          className="mt-20 bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-700"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-10 lg:p-16">
              <div className="text-sm font-light text-violet-400 mb-3">Our Impact</div>
              <h3 className="text-3xl font-light text-white mb-6">We're revolutionizing recruiting workflows</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-8 gap-x-12">
                <div>
                  <div className="text-4xl font-light text-violet-400">70%</div>
                  <p className="text-gray-400 mt-2 font-light">Reduction in administrative tasks for recruiting teams</p>
                </div>
                <div>
                  <div className="text-4xl font-light text-violet-400">60%</div>
                  <p className="text-gray-400 mt-2 font-light">Faster time-to-hire with AI-powered workflows</p>
                </div>
                <div>
                  <div className="text-4xl font-light text-violet-400">5,000+</div>
                  <p className="text-gray-400 mt-2 font-light">Hours saved monthly for our enterprise clients</p>
                </div>
                <div>
                  <div className="text-4xl font-light text-violet-400">47%</div>
                  <p className="text-gray-400 mt-2 font-light">Improvement in candidate response rates to outreach</p>
                </div>
              </div>
            </div>
            <div className="relative h-96 lg:h-auto">
              <img 
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&h=600&q=80" 
                alt="Modern office space with diverse team" 
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-violet-900 to-transparent opacity-60"></div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
